import { useCallback, type RefObject } from 'react'

import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'
import { useGetGlobalSearchResultsPresenter } from './useGetGlobalSearchResultsPresenter'
import { GuidedIntakePromoBanner } from '../../../../components/GuidedIntakePromoBanner'
import { LlmConversationConfirmation } from '../../../../components/LlmConversationConfirmation'
import { LlmConversationConfirmationBanner } from '../../../../components/LlmConversationConfirmationBanner'
import { useGuidedIntakePromoDismissal } from '../../../../hooks/useGuidedIntakePromoDismissal'
import { useTrackGuidedIntakeEntryPointShown } from '../../../../hooks/useTrackGuidedIntakeEntryPointShown'
import { GuidedIntakeEntryPoints } from '../../../../value-objects/enums'
import { type RecentConversation } from '../../../../value-objects/RecentConversation'
import { ContentStates } from '../../enums/ContentStateEnum'
import { LlmConfirmationModes } from '../../enums/LlmConfirmationModeEnum'
import { GlobalSearchView } from '../../views/GlobalSearchView/GlobalSearchView'

interface IGetGlobalSearchResultsPresenterProps {
  onClose: () => void
  inputRef?: RefObject<HTMLInputElement>
  onHandoff?: (query: string) => void
  showGuidedIntakePromo?: boolean
  onGuidedIntakePromoClick: () => void
  onResumeConversation?: (conversationId: string) => void
  onStartGuidedIntake?: () => void
}

export const GetGlobalSearchResultsPresenter = ({
  onClose,
  inputRef: externalInputRef,
  onHandoff,
  showGuidedIntakePromo = false,
  onGuidedIntakePromoClick,
  onResumeConversation,
  onStartGuidedIntake,
}: IGetGlobalSearchResultsPresenterProps) => {
  const intl = useProcurifyIntl()
  const {
    commands: {
      changeQuery,
      openHighlightedOrSubmit,
      openRecentItem,
      openResultItem,
      refreshRecentConversation,
      selectTab,
      navigateDown,
      navigateUp,
    },
    data: {
      query,
      submittedQuery,
      recentItems,
      recentConversations,
      resultItems,
      tabs,
      inputRef,
      highlightedIndex,
      activeDescendantId,
      isExpanded,
      originalQuery,
    },
    states: {
      showEnterHint,
      contentState,
      selectedTab,
      showTruncatedNotice,
      noResultsMessage,
      unsupportedQueryMessage,
      errorMessage,
      llmConfirmationMode,
    },
  } = useGetGlobalSearchResultsPresenter(onClose, externalInputRef)

  const { isDismissed: isPromoDismissed } = useGuidedIntakePromoDismissal()

  const handleConfirmationAccept = useCallback(() => {
    onHandoff?.(originalQuery ?? submittedQuery)
  }, [onHandoff, originalQuery, submittedQuery])

  // The view swaps the whole content area for the paywall banner, so a
  // dismissed promo has to read as absent here rather than render nothing.
  const showPromo = showGuidedIntakePromo && !isPromoDismissed

  const isReplacingResults =
    llmConfirmationMode === LlmConfirmationModes.REPLACES_RESULTS
  const isWithResults =
    llmConfirmationMode === LlmConfirmationModes.WITH_RESULTS

  const showWorkflows =
    onStartGuidedIntake !== undefined &&
    !(isReplacingResults && onHandoff) &&
    query === '' &&
    (contentState === null || contentState === ContentStates.RECENTS)

  useTrackGuidedIntakeEntryPointShown(
    GuidedIntakeEntryPoints.SEARCH_OVERLAY,
    showWorkflows
  )

  const paywallBanner = showPromo ? (
    <GuidedIntakePromoBanner
      analyticsId='global-command-gi-promo-banner-paywall'
      onLearnMoreClick={onGuidedIntakePromoClick}
    />
  ) : undefined

  const withResultsBanner = onHandoff ? (
    <LlmConversationConfirmationBanner
      title={intl.formatMessage(messages.confirmationTitle)}
      actionContent={intl.formatMessage(messages.confirmationAction)}
      actionAriaLabel={intl.formatMessage(messages.confirmationAction)}
      actionAnalyticsId='global-command-llm-confirmation-results-action'
      onActionClick={handleConfirmationAccept}
    />
  ) : (
    paywallBanner
  )

  const promoBanner = showPromo ? (
    <GuidedIntakePromoBanner
      analyticsId='global-command-gi-promo-banner-default'
      onLearnMoreClick={onGuidedIntakePromoClick}
    />
  ) : undefined

  const handleRecentConversationClick = useCallback(
    (conversation: RecentConversation) => {
      refreshRecentConversation(conversation)
      onResumeConversation?.(conversation.conversationId)
    },
    [refreshRecentConversation, onResumeConversation]
  )

  if (isReplacingResults && onHandoff) {
    return (
      <LlmConversationConfirmation
        query={query}
        inputRef={inputRef}
        title={intl.formatMessage(messages.confirmationTitle)}
        actionContent={intl.formatMessage(messages.confirmationAction)}
        actionAriaLabel={intl.formatMessage(messages.confirmationAction)}
        actionAnalyticsId='global-command-llm-confirmation-action'
        onChange={changeQuery}
        onEnter={openHighlightedOrSubmit}
        onActionClick={handleConfirmationAccept}
      />
    )
  }

  return (
    <GlobalSearchView
      query={query}
      showEnterHint={showEnterHint}
      contentState={contentState}
      recentItems={recentItems}
      recentConversations={recentConversations}
      resultItems={resultItems}
      tabs={tabs}
      selectedTab={selectedTab}
      showTruncatedNotice={showTruncatedNotice}
      noResultsMessage={noResultsMessage}
      unsupportedQueryMessage={unsupportedQueryMessage}
      errorMessage={errorMessage}
      inputRef={inputRef}
      highlightedIndex={highlightedIndex}
      activeDescendantId={activeDescendantId}
      isExpanded={isExpanded}
      promoBanner={promoBanner}
      paywallBanner={isReplacingResults ? paywallBanner : undefined}
      resultsBanner={isWithResults ? withResultsBanner : undefined}
      showWorkflows={showWorkflows}
      onQueryChange={changeQuery}
      onEnter={openHighlightedOrSubmit}
      onNavigateDown={navigateDown}
      onNavigateUp={navigateUp}
      onRecentItemClick={openRecentItem}
      onResultItemClick={openResultItem}
      onTabChange={selectTab}
      onStartGuidedIntake={onStartGuidedIntake}
      onRecentConversationClick={
        onResumeConversation ? handleRecentConversationClick : undefined
      }
    />
  )
}
